/**
 * Filesystem fingerprint — superblock/BPB sniffer.
 *
 * Identifies the filesystem TYPE on a partition. Does not mount, does not
 * read contents. Used by the inspector to label each partition.
 *
 * Surface: window.__DiskImage.fingerprint(reader, lbaStart, lbaEnd?, lbs?)
 *   → Promise<{ fs: string, version?: string, label?: string, uuid?: string, detail?: object }>
 *
 * Detected: FAT12, FAT16, FAT32, exFAT, NTFS, ext2/3/4, HFS+, APFS, ReFS,
 *           XFS, Btrfs, BitLocker (FVE-FS), LUKS, ISO9660 (mounted partition),
 *           UDF (NSR markers), swap.
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.fingerprint) return;

    function _ascii(b, off, len) {
        var s = '';
        for (var i = 0; i < len; i++) {
            var c = b[off + i];
            if (c === 0) break;
            s += String.fromCharCode(c);
        }
        return s.replace(/\s+$/, '');
    }
    function _eq(b, off, str) {
        for (var i = 0; i < str.length; i++) if (b[off + i] !== str.charCodeAt(i)) return false;
        return true;
    }
    function _u16le(b, off) { return b[off] | (b[off + 1] << 8); }
    function _u16be(b, off) { return (b[off] << 8) | b[off + 1]; }
    function _u32le(b, off) { return ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0); }

    function _hex(n, w2) { var s = (n >>> 0).toString(16); while (s.length < w2) s = '0' + s; return s; }

    function fingerprint(reader, lbaStart, lbaEnd, lbs) {
        lbs = lbs || 512;
        var off = lbaStart * lbs;
        var probeLen = Math.min(reader.size - off, 64 * 1024);
        if (probeLen < 1024) return Promise.resolve({ fs: 'unknown', detail: { reason: 'too-short' } });
        return reader.bytes(off, probeLen).then(function (b) {
            // BitLocker
            if (_eq(b, 3, '-FVE-FS-') || _eq(b, 0x70, 'NTFS    ') === false && b[0] === 0xeb && b[1] === 0x52 && b[2] === 0x90 && _ascii(b, 3, 8) === '-FVE-FS-') {
                return { fs: 'bitlocker', detail: { signature: 'FVE-FS' } };
            }
            // LUKS
            if (b[0] === 0x4c && b[1] === 0x55 && b[2] === 0x4b && b[3] === 0x53 && b[4] === 0xba && b[5] === 0xbe) {
                return {
                    fs: 'luks',
                    version: _u16be(b, 6) + '.0',
                    detail: { uuid: _ascii(b, 0xA8, 40) }
                };
            }
            // NTFS — OEM ID at offset 3 = "NTFS    "
            if (_eq(b, 3, 'NTFS    ')) {
                var bps = _u16le(b, 11);
                var spc = b[13];
                var totalSectors = _readU64le(b, 0x28);
                var mftCluster = _readU64le(b, 0x30);
                var serial = '';
                for (var i = 0; i < 8; i++) serial += _hex(b[0x48 + 7 - i], 2);
                return {
                    fs: 'ntfs',
                    detail: {
                        bytesPerSector: bps,
                        sectorsPerCluster: spc,
                        totalSectors: totalSectors,
                        mftCluster: mftCluster,
                        serial: serial.slice(0, 8) + '-' + serial.slice(8)
                    }
                };
            }
            // exFAT
            if (_eq(b, 3, 'EXFAT   ')) {
                return {
                    fs: 'exfat',
                    version: _u16le(b, 104) + '.' + _u16le(b, 102) ? '1.0' : '1.0',
                    detail: { volumeSerial: _hex(_u32le(b, 100), 8) }
                };
            }
            // FAT — BPB OEM ID + FAT signature
            if (b[510] === 0x55 && b[511] === 0xAA) {
                var fatType = _ascii(b, 0x36, 8);
                var fatType32 = _ascii(b, 0x52, 8);
                if (fatType === 'FAT12   ') return _fatInfo(b, 'fat12');
                if (fatType === 'FAT16   ') return _fatInfo(b, 'fat16');
                if (fatType32 === 'FAT32   ') return _fatInfo(b, 'fat32');
                // Heuristic by media descriptor
                var bps2 = _u16le(b, 11);
                var spc2 = b[13];
                if (bps2 === 512 && (spc2 & (spc2 - 1)) === 0) {
                    var fatSize16 = _u16le(b, 22);
                    if (fatSize16 === 0) return _fatInfo(b, 'fat32');
                }
            }
            // ext2/3/4 — superblock at offset 1024 in partition
            if (probeLen >= 1024 + 1024) {
                var sb = b.subarray(1024, 1024 + 1024);
                if (sb[0x38] === 0x53 && sb[0x39] === 0xEF) {
                    var rev = _u32le(sb, 0x4C);
                    var compatFeatures = _u32le(sb, 0x5C);
                    var incompatFeatures = _u32le(sb, 0x60);
                    var roCompatFeatures = _u32le(sb, 0x64);
                    var fs = 'ext2';
                    if (compatFeatures & 0x04) fs = 'ext3'; // has_journal
                    if (incompatFeatures & 0x040 || incompatFeatures & 0x200 || roCompatFeatures & 0x40) fs = 'ext4';
                    var label = '';
                    for (var k = 0x78; k < 0x88; k++) {
                        if (sb[k] === 0) break;
                        label += String.fromCharCode(sb[k]);
                    }
                    var uuidB = sb.subarray(0x68, 0x68 + 16);
                    var uuid = _hex(uuidB[0], 2) + _hex(uuidB[1], 2) + _hex(uuidB[2], 2) + _hex(uuidB[3], 2) + '-' +
                               _hex(uuidB[4], 2) + _hex(uuidB[5], 2) + '-' +
                               _hex(uuidB[6], 2) + _hex(uuidB[7], 2) + '-' +
                               _hex(uuidB[8], 2) + _hex(uuidB[9], 2) + '-' +
                               _hex(uuidB[10], 2) + _hex(uuidB[11], 2) + _hex(uuidB[12], 2) + _hex(uuidB[13], 2) + _hex(uuidB[14], 2) + _hex(uuidB[15], 2);
                    return { fs: fs, label: label, uuid: uuid, detail: { revLevel: rev } };
                }
            }
            // HFS+: 'H+' or 'HX' at offset 0x400
            if (probeLen >= 0x400 + 4) {
                var hfsSig = String.fromCharCode(b[0x400], b[0x401]);
                if (hfsSig === 'H+' || hfsSig === 'HX') {
                    return { fs: 'hfsplus', detail: { signature: hfsSig, version: _u16be(b, 0x402) } };
                }
            }
            // APFS container superblock magic 'NXSB' at offset 32
            if (probeLen >= 36 && _eq(b, 32, 'NXSB')) {
                return { fs: 'apfs', detail: { signature: 'NXSB' } };
            }
            // ReFS: signature 'ReFS\0\0\0\0' at offset 3
            if (_eq(b, 3, 'ReFS\u0000\u0000\u0000\u0000')) {
                return { fs: 'refs', detail: { signature: 'ReFS' } };
            }
            // XFS magic 'XFSB' at offset 0
            if (_eq(b, 0, 'XFSB')) {
                return { fs: 'xfs', detail: { signature: 'XFSB' } };
            }
            // Btrfs: magic '_BHRfS_M' at offset 0x10040
            if (probeLen >= 0x10040 + 8 && _eq(b, 0x10040, '_BHRfS_M')) {
                return { fs: 'btrfs', detail: { signature: '_BHRfS_M' } };
            }
            // ISO9660 inside partition (sometimes happens): "CD001" at offset 0x8001
            if (probeLen >= 0x8006 && _eq(b, 0x8001, 'CD001')) {
                return { fs: 'iso9660', detail: { signature: 'CD001' } };
            }
            // UDF: NSR0[2-3] inside partition at sector 18
            if (probeLen >= 0x9006 && (_eq(b, 0x9001, 'NSR02') || _eq(b, 0x9001, 'NSR03'))) {
                return { fs: 'udf', detail: { signature: _ascii(b, 0x9001, 5) } };
            }
            // Linux swap: "SWAPSPACE2" at offset 0xFF6 (page-1 aligned for 4 KB pages)
            if (probeLen >= 0x1000 && _eq(b, 0xFF6, 'SWAPSPACE2')) {
                return { fs: 'swap', detail: { signature: 'SWAPSPACE2' } };
            }
            return { fs: 'unknown' };
        }).catch(function (err) {
            return { fs: 'unknown', detail: { error: String(err) } };
        });
    }

    function _readU64le(b, off) {
        var lo = ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0);
        var hi = ((b[off + 4] | (b[off + 5] << 8) | (b[off + 6] << 16) | (b[off + 7] << 24)) >>> 0);
        return hi * 0x100000000 + lo;
    }

    function _fatInfo(b, fs) {
        var oem = _ascii(b, 3, 8);
        var bps = _u16le(b, 11);
        var spc = b[13];
        var reserved = _u16le(b, 14);
        var totalSectors16 = _u16le(b, 19);
        var totalSectors32 = _u32le(b, 32);
        var label = (fs === 'fat32') ? _ascii(b, 0x47, 11) : _ascii(b, 0x2B, 11);
        var serial = (fs === 'fat32') ? _u32le(b, 0x43) : _u32le(b, 0x27);
        return {
            fs: fs,
            label: label.replace(/\s+$/, ''),
            uuid: _hex(serial, 8).slice(0, 4) + '-' + _hex(serial, 8).slice(4),
            detail: {
                oem: oem,
                bytesPerSector: bps,
                sectorsPerCluster: spc,
                reserved: reserved,
                totalSectors: totalSectors32 || totalSectors16
            }
        };
    }

    w.__DiskImage.fingerprint = fingerprint;
})(window);
